import React, { useState } from 'react'
import {
  defaultItemIcon,
} from '../../../icons'
import { getFromNowAsText } from '../../../helpers/timeHelper'
import {
  getStaleDetectionWarningColor,
  getStaleDetectionWarningMessage,
  shouldShowStaleDetectionWarning,
} from '../../../helpers/staleDetectionWarning'
import {
  getUserName,
  getLastStatus,
  getLostFoundStatus,
  getMamorioImageSrc,
  generateMamorioListCSV,
} from '../../../helpers/CommonMamorioHelper'
import EmptyListView from './EmptyMamorioList'
import { showLostAndBattery } from '../../../modules/common/session'
import DetailMamorioModal from '../../overview/DetailMamorioModal'
import { Filter } from '../../../modules/Mamorio'
import HeaderType from '../../../modules/common/headerType'
import getSortIcon from '../../../modules/common/sortUtil'
import { useAppDispatch } from '../../../hooks'
import { closeModal as closeModalAction, showModal } from '../../../modules/UI'
import { CONFIRM_MODAL, ERROR_MODAL } from '../../common/RootModal'
import { returnRental } from '../../../modules/Rental'
import { getErrorString } from '../../../utils/errorUtils'
import LastEventLogSummary from '../../common/LastEventLogSummary'
import BatteryRemaining from '../../common/BatteryRemaining'

type Props = {
  group: IGroup
  mamorios: IMamorio[] | undefined | null
  filter: Filter
  onChangeSort: (type: HeaderType) => void
  rentalMode: boolean
}

const MamorioListOnBizPlus = ({
  group,
  mamorios,
  filter,
  onChangeSort,
  rentalMode,
}: Props) => {
  const dispatch = useAppDispatch()
  const memoEnabled = group.memo_enabled
  const lastEventLogEnabled = group.last_event_log_enabled ?? false
  const [selectedMamorio, setSelectedMamorio] = useState<IMamorio | null>(null)
  const closeModal = () => {
    setSelectedMamorio(null)
  }

  const downloadCsv = async () => {
    await generateMamorioListCSV(mamorios ?? [], { includeRentalStatus: rentalMode })
  }

  const onModalError = (message: string) => {
    if (message === null || message === undefined) return

    dispatch(closeModalAction({ modalType: ERROR_MODAL }))

    dispatch(showModal({
      modalType: ERROR_MODAL,
      modalProps: {
        message,
      },
    }))
  }

  const onClickForceReturnRental = async (targetMamorio: IMamorio, detailViewCallback: VoidFunction) => {
    if (!group.id) return
    dispatch(showModal({
      modalType: CONFIRM_MODAL,
      modalProps: {
        message: '強制的に返却すると、持ち出しが解除され<br />所有者がオーナーに戻ります。よろしいですか？',
        confirmButtonText: '強制的に返却する',
        onActionConfirmed: async () => {
          try {
            await dispatch(returnRental({
              groupId: group.id,
              mamorioId: targetMamorio.id,
              filter,
            })).unwrap()
            detailViewCallback()
          } catch (e: unknown) {
            onModalError(getErrorString(e))
          } finally {
            dispatch(closeModalAction({ modalType: CONFIRM_MODAL }))
          }
        },
      },
    }))
  }

  if (!mamorios || mamorios.length === 0) {
    return (<EmptyListView />)
  }

  const cursor = 'pointer'
  const icon = getSortIcon(filter.sort, filter.order)

  return (
    <div className="mamorio-settings">
      <table className="table">
        <thead className="header" data-testid="mamorio-item-list-header">
          <tr>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onChangeSort(HeaderType.Name)}
              >
                MAMORIOの名前
                <img alt="sort" src={icon.name} />
              </div>
            </th>
            {memoEnabled && (
              <th>
                <div
                  className="title"
                  style={{ cursor, minWidth: '40px' }}
                  onClick={() => onChangeSort(HeaderType.Memo)}
                >
                  メモ
                  <img alt="sort" src={icon.memo} />
                </div>
              </th>
            )}
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onChangeSort(HeaderType.Time)}
              >
                最終検知時刻
                <img alt="sort" src={icon.time} />
              </div>
            </th>
            <th>
              <div
                className="title"
                style={{ cursor }}
                onClick={() => onChangeSort(HeaderType.LastDetectedDevice)}
              >
                最後に検知した端末
                <img alt="sort" src={icon.lastDetectedDevice} />
              </div>
            </th>
            {lastEventLogEnabled && (
              <th>
                <div
                  className="title"
                  style={{ cursor, minWidth: '75px' }}
                  onClick={() => onChangeSort(HeaderType.LastEventLogName)}
                >
                  最新イベント
                  <img alt="sort" src={icon.lastEventLogName} />
                </div>
              </th>
            )}
            <th>
              <div
                className="title"
                style={{ cursor, minWidth: '75px' }}
                onClick={() => onChangeSort(HeaderType.BatteryLifeRessetedAt)}
              >
                バッテリー
                <img alt="sort" src={icon.batteryLifeResettedAt} />
              </div>
            </th>
            {showLostAndBattery() && (
              <th>
                <div
                  className="title"
                  style={{ cursor, minWidth: '40px' }}
                  onClick={() => onChangeSort(HeaderType.Status)}
                >
                  状態
                  <img alt="sort" src={icon.status} />
                </div>
              </th>
            )}
            <th>
              <div className="title">
                所持者
              </div>
            </th>
          </tr>
        </thead>
        <tbody className="list" data-testid="mamorio-item-list-body">
          {
            mamorios.map((mamorio: IMamorio) => {
              const shouldShowWarning = shouldShowStaleDetectionWarning(mamorio.recent_log?.at, group)

              return (
                <tr
                  key={mamorio.id}
                  className="mamorio-item-list-item"
                  onClick={() => setSelectedMamorio(mamorio)}
                >
                  <td className="item mamorio-item">
                    <img width={40} className="image" src={getMamorioImageSrc(mamorio, defaultItemIcon)} alt="member" onError={(e) => { e.currentTarget.src = defaultItemIcon }} />
                    <span className="name">
                      {mamorio.name}
                    </span>
                  </td>
                  {memoEnabled && (
                    <td className="item mamorio-item">
                      <span className="name">
                        {mamorio.memo ? mamorio.memo : '-'}
                      </span>
                    </td>
                  )}
                  <td
                    className={`item item-last-detected-at${shouldShowWarning ? ' stale-detection-warning' : ''}`}
                    style={shouldShowWarning ? { color: getStaleDetectionWarningColor(group) } : undefined}
                  >
                    <div>
                      {mamorio?.recent_log ? getFromNowAsText(mamorio.recent_log.at) : '-'}
                    </div>
                    {shouldShowWarning && (
                      <div
                        className="stale-detection-warning-message"
                        style={{ fontSize: '14px' }}
                      >
                        {getStaleDetectionWarningMessage(group)}
                      </div>
                    )}
                  </td>
                  <td className="item item-detected-by">
                    <img className="item-icon" src={getLastStatus(mamorio).imgSrc} style={{ borderRadius: 0 }} alt="status" />
                    {getLastStatus(mamorio).text}
                  </td>

                  {lastEventLogEnabled && (
                    <td className="item item-last-event-log">
                      <LastEventLogSummary lastEventLog={mamorio.last_event_log} />
                    </td>
                  )}

                  {/* ----- バッテリー残量 ----- */}
                  <td className="item item-detected-by">
                    <BatteryRemaining percentage={mamorio.estimated_remain_battery} />
                  </td>

                  {/* ----- 紛失ステータス ----- */}
                  {showLostAndBattery() && (
                    <td className="item item-detected-by">
                      <div className="img-name-item">
                        {getLostFoundStatus(mamorio)}
                      </div>
                    </td>
                  )}
                  <td className="item">
                    {mamorio.is_rent
                      ? (
                        <div className="sub">
                          <div className="status-rental rental">
                            持出中
                          </div>
                          <div className="rental-owner">
                            {mamorio.user ? getUserName(mamorio.user) : '所有者がいません'}
                          </div>
                        </div>
                      )
                      : (
                        <div className="sub">
                          <span className="rental-owner">
                            { mamorio.user ? getUserName(mamorio.user) : '-' }
                          </span>
                        </div>
                      )}
                  </td>
                </tr>
              )
            })
          }
        </tbody>
      </table>
      <div className="mamorio-settings__index-footer">
        <div className="mamorio-settings__download-links">
          <span>ダウンロード:</span>
          <button
            type="button"
            className="mamorio-settings__download-link"
            onClick={downloadCsv}
          >
            CSV
          </button>
        </div>
      </div>
      {selectedMamorio && (
        <DetailMamorioModal
          modalProps={{
            mamorio: selectedMamorio,
            group,
            onRentalForceReturnClick: (callback: VoidFunction) => onClickForceReturnRental(selectedMamorio, callback),
          }}
          closeModalFunction={closeModal}
        />
      )}
    </div>
  )
}

export default MamorioListOnBizPlus
